/**
 * The My Teaching shell.
 *
 * It owns the one query for the classes you teach and publishes the sidebar
 * group, then hands both to whichever section is showing: the overview, or a
 * category gathered across every class.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useOutletContext, Outlet } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '~/components/ui/card';
import type { AppLayoutContext } from '~/types';
import { TEACHING_SECTIONS, useContextNav } from '~/components/Sidebar/contextNav';
import { useMyTeachingClassesQuery } from './useMyTeachingClassesQuery';
import type { TeachingContext } from './teachingContext';

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function clampDateToYear(value: string, startsOn?: string, endsOn?: string) {
  if (!startsOn || !endsOn) return value;
  if (value < startsOn || value > endsOn) return startsOn;
  return value;
}

/** The viewer's own wall clock, HH:MM, so the server answers in their day. */
function localTimeString(date = new Date()) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export default function Teaching() {
  const { schoolSlug, yearSlug } = useParams();
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const { setContextNav } = useContextNav();
  const [rollDate, setRollDate] = useState(localDateString);
  const today = useMemo(localDateString, []);
  const nowTime = useMemo(localTimeString, []);

  const schoolYear = useMemo(
    () => school?.schoolYears?.find((y) => y.slug === yearSlug),
    [school, yearSlug]
  );

  const schoolYearStart = schoolYear?.startDate ? String(schoolYear.startDate) : undefined;
  const schoolYearEnd = schoolYear?.endDate ? String(schoolYear.endDate) : undefined;
  const effectiveRollDate = clampDateToYear(rollDate, schoolYearStart, schoolYearEnd);

  useEffect(() => setRollDate(effectiveRollDate), [effectiveRollDate, schoolYear?.id]);

  const { data, loading, error } = useMyTeachingClassesQuery(schoolYear?.id, effectiveRollDate, nowTime);
  const classes = useMemo(() => data?.schoolYear?.myClassOfferings ?? [], [data]);
  const attendanceRolls = useMemo(() => data?.schoolYear?.myAttendanceRolls ?? [], [data]);
  const inboxItems = useMemo(() => data?.schoolYear?.myTeachingInbox?.items ?? [], [data]);

  const basePath = `/app/${schoolSlug}/years/${yearSlug}/teaching`;
  const classCount = classes.length;

  // Teaching is a place you work inside, the same way a class is, so it takes
  // the whole sidebar. Someone who also administers the school needs a way back
  // to that work; a teacher is already home and gets no back link.
  const isAdmin = roleContext?.selectedRole === 'PRIMARY_ADMIN' || roleContext?.selectedRole === 'ADMIN';
  // Memoised because it is published through an effect: a fresh object every
  // render would republish the group on every render.
  const backLink = useMemo(
    () => (isAdmin ? { path: `/app/${schoolSlug}/dashboard`, label: 'Back to admin' } : undefined),
    [isAdmin, schoolSlug]
  );

  // Publish the teaching group to the sidebar, which sits above this outlet.
  useEffect(() => {
    if (!schoolSlug || !yearSlug) return undefined;
    setContextNav({
      kind: 'teaching',
      title: 'My Teaching',
      subtitle: classCount === 1 ? '1 class' : `${classCount} classes`,
      basePath,
      sections: TEACHING_SECTIONS,
      backLink,
      exclusive: true,
    });
    return () => setContextNav(null);
  }, [schoolSlug, yearSlug, basePath, classCount, backLink, setContextNav]);

  const outletContext = useMemo<TeachingContext>(() => ({
    classes,
    attendanceRolls,
    inboxItems,
    loading,
    error,
    schoolYearName: schoolYear?.name ?? '',
    schoolYearStart,
    schoolYearEnd,
    rollDate: effectiveRollDate,
    setRollDate,
    today,
    nowTime,
  }), [classes, attendanceRolls, inboxItems, loading, error, schoolYear?.name, schoolYearStart, schoolYearEnd, effectiveRollDate, today, nowTime]);

  if (!school) return <div className="p-6"><p>Loading…</p></div>;
  if (!schoolYear) {
    return (
      <div className="p-6">
        <Card><CardHeader><CardTitle>School year not found</CardTitle></CardHeader></Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-3xl font-bold">My Teaching</h1>
          <p className="text-muted-foreground">
            Your day and what's outstanding in {schoolYear.name}
          </p>
        </div>
      </div>

      <Outlet context={outletContext} />
    </div>
  );
}
